import { useMemo, useState } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  PeriodSelector,
  Skeleton,
  type PeriodOption,
} from '@atrvd/saas-ui'
import { hasPermission } from '@dcp/shared'
import { businessDate } from '@dcp/shared/utils/business-date'
import dayjs from '@dcp/shared/utils/dayjs'
import { cashPositionPeriods, type CashPositionPeriod } from '@/lib/date-utils'
import {
  useCashPositionSummaryMultiPeriod,
  type CashPositionMultiPeriodColumn,
} from '@/hooks/useCashPositionSummaryMultiPeriod'
import {
  useFacilities,
  usePacks,
  useLocTransactions,
  useLocAvailability,
  type LocTxnScope,
} from '@/hooks/useBanking'
import { useCashPositionFreshness } from '@/hooks/useCashPositionFreshness'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useProfile } from '@/hooks/useProfile'
import { CashPositionSummaryGrid, CashPositionFilters } from '@/components/banking'
import { CashPositionDownloadMenu } from '@/components/banking/CashPositionDownloadMenu'
import { buildCashPositionReport } from '@/components/banking/cashPositionReport'
import { cashPositionFilterText } from '@/components/banking/cashPositionDownload'
import { buildCashPositionWarnings } from '@/components/banking/cashPositionWarnings'
import { ViewBankTransactionsDialog } from '@/components/banking/ViewBankTransactionsDialog'
import { ManageProjectionsDialog } from '@/components/banking/manage-projections/ManageProjectionsDialog'
import {
  cellTargetKey,
  projectionCellTarget,
  type ProjectionCellRow,
  type ProjectionCellScope,
} from '@/lib/projection-cell'
import {
  cashPositionQueryFilters,
  NO_CASH_POSITION_FILTERS,
  selectCashPositionFacilities,
  selectCashPositionPacks,
  tickedFacilityIds,
  shouldShowCombinedTotal,
  type CashPositionFilterState,
} from '@/components/banking/cashPositionColumns'
import {
  facilitiesForPackSelection,
  NO_PACK,
  NO_PACK_LABEL,
} from '@/components/ledgersync/connectionManagement.utils'

/** Prefix marking a pack-subtotal column key (`pack:<pack_id>`), per
 *  useCashPositionSummaryMultiPeriod. Stripped to recover the raw pack id. */
const PACK_KEY_PREFIX = 'pack:'

// DCP-333: `collectFacilityTxns` and `groupPackByFacility` used to live here,
// slicing this page's drill-downs out of an org-wide transaction feed held in
// memory. Both the facility/pack scoping and the group-by-facility + dedupe-by-id
// logic they carried now happen in `useLocTransactions` — server-side filtering
// per pop-up, which is what fixes the PostgREST 1000-row truncation that left
// pop-ups empty. Do not reintroduce a client-side variant: two copies of that
// grouping would let this page and LocAvailabilityPage drift apart again.

/** Case-insensitive label comparator for stable ASC option ordering (AC.5). */
const byLabel = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })

/**
 * Period selection driving which forecast window(s) are shown. `'all'` renders
 * every period stacked; each other value is one of the {@link cashPositionPeriods}
 * keys, rendering exactly that period (DCP-144).
 */
type PeriodSelection = 'all' | CashPositionPeriod['key']

// The ordered 5-period ladder (remainder + 4 full weeks) — the single source of
// truth for both the period selector's options and the single-period index
// lookup below, so the labels/keys never drift from {@link cashPositionPeriods}.
const periods = cashPositionPeriods()

/** Segmented-control options, in display order (All + the five periods), derived
 *  from the period ladder so labels stay in sync with {@link cashPositionPeriods}. */
const PERIOD_OPTIONS: PeriodOption[] = [
  { value: 'all', label: 'All' },
  ...periods.map((p) => ({ value: p.key, label: p.label })),
]

/**
 * Cash Position Summary page (DCP-143 + DCP-145 + DCP-144 + DCP-293).
 *
 * A 5-period cash-flow forecast: the remainder of the current week plus the four
 * following full calendar weeks. Facilities and pack subtotals render as columns;
 * the row-label column and a field-wise "Combined Total" column are pinned on the
 * LEFT while the data columns scroll horizontally inside the grid's container. The
 * Combined Total is dropped when the report is down to a single pack or
 * facility, where it would only duplicate an existing column (`showCombinedTotal`).
 *
 * A period segmented control scopes the view to "All" (all five periods stacked)
 * or a single period. Two multi-select FILTERS (DCP-145, DCP-429): Packs set the
 * scope ("No Pack" = the facilities without one), Facilities narrow it — a
 * picked pack comes in with all its facilities ticked; a pack keeps its
 * subtotal only while all its members are shown; empty selection = "All". ALL
 * computation — per-facility math, pack subtotals, the filter-aware Combined
 * Total, cross-period chaining of the ending balance, filtering and ordering —
 * happens server-side in the `get_cash_position_summary_multi_period` RPC (via
 * {@link useCashPositionSummaryMultiPeriod}). The page only owns selection state,
 * the filter-option lists, and load/empty/error chrome.
 *
 * "Download report" exports exactly what is on screen — the loaded data, the
 * current filters and the selected period — as PDF or XLSX; it makes no request
 * of its own. There are no Add-projection or Send actions.
 */
export function CashPositionPage() {
  const [filters, setFilters] = useState<CashPositionFilterState>(NO_CASH_POSITION_FILTERS)
  // Period selection (default "All" — every forecast window stacked).
  const [periodSelection, setPeriodSelection] = useState<PeriodSelection>('all')
  const { data: facilities, isLoading: facilitiesLoading, error: facilitiesError } = useFacilities()
  // Active facilities drive the "org has facilities" check, the filter options
  // and the pack membership the filters resolve against. The RPC returns a row
  // for every active facility, so this matches the renderable set.
  const activeFacilities = useMemo(() => (facilities ?? []).filter((f) => f.is_active), [facilities])

  // `isPlaceholderData` is true while a filter/period refetch is in flight and
  // the grid is still showing the PREVIOUS result (kept via placeholderData) —
  // used to dim the grid so a slow RPC doesn't read as "the filter did nothing".
  // `isFetching` (DCP-333, distinct from `isPlaceholderData` above) is true for
  // ANY fetch in flight — including a background settle-time refetch after a
  // save, where `isPlaceholderData` is already false because the NEW result has
  // landed for dimming purposes but the drill's expectedTotal check below still
  // needs to know a fetch is active.
  const {
    data,
    isLoading,
    isPlaceholderData,
    isFetching: summaryFetching,
    error: summaryError,
  } = useCashPositionSummaryMultiPeriod(cashPositionQueryFilters(filters, activeFacilities))
  const { currentOrganization } = useOrganization()
  const { data: packs, isLoading: packsLoading, error: packsError } = usePacks()

  // Per-facility missing-data signals for the balances block (DCP-161). Fetched
  // separately from the summary RPC but under the same query-key prefix, so every
  // mutation that fixes the data already clears the warnings (AC#3).
  const { data: freshness } = useCashPositionFreshness()

  // A pack SUBTOTAL column aggregates its members' signals by `column.packId`,
  // which the summary RPC stamped on the very rows it summed the subtotal from
  // — the single source of truth for pack membership here. Deliberately NOT a
  // facility → pack map built from `useFacilities`: the subtotal FIGURE is a
  // `SUM` computed inside that same RPC, so deriving the WARNING's membership
  // from a second, independently-loaded source would make the two agree only
  // by coincidence (see `CashPositionMultiPeriodColumn.packId`'s doc comment).
  const warnings = useMemo(
    () =>
      buildCashPositionWarnings({
        // `undefined` (not yet loaded) and a failed fetch both mean "say nothing".
        freshness: freshness ?? null,
        columns: data?.columns ?? [],
      }),
    [freshness, data?.columns],
  )

  // Real per-reference LOC starting-usage baselines (DCP-169, PR #169 review):
  // the drill modal shows the ACTUAL `starting_loc_usage_amount` from
  // get_loc_availability — the same field/RPC LocAvailabilityPage reads — rather
  // than reconstructing it as `locUsage − Σtx`. Keyed by reference id: a pack id
  // or a standalone facility id. A pack MEMBER facility is never a reference, so
  // a lookup for its column misses → 0, exactly the BA rule (member = tx-only).
  // `isFetching` (DCP-333) — see the drill's `expectedTotal` prop below for why
  // this query's in-flight state also gates that check, alongside the summary
  // query's own `summaryFetching`.
  // `error` (DCP-333, Presto PR #184 finding 3): read so a FIRST-load failure
  // can be surfaced to the user instead of silently making every "View LOC
  // transactions" button vanish forever (React Query leaves `data` undefined on
  // a failed first load, and `drillReady` below never flips true). See the
  // notice rendered near the grid for where this is shown — and note it is
  // gated on `!locReferences` as well, since a failed BACKGROUND refetch keeps
  // `data` and therefore keeps the drill working (PR #184 human review).
  const {
    data: locReferences,
    isFetching: locAvailabilityFetching,
    error: locAvailabilityError,
  } = useLocAvailability()
  const startingByRef = useMemo(() => {
    const map = new Map<string, number>()
    for (const ref of locReferences ?? []) {
      map.set(
        ref.id,
        ref.lines.reduce((sum, line) => sum + (line.startingLocUsageAmount ?? 0), 0),
      )
    }
    return map
  }, [locReferences])

  // Active drill-down (the modal is closed when null). `referenceName` is the
  // bare column name — the dialog itself prefixes "LOC Usage for …".
  //
  // DCP-333: holds the clicked column's SCOPE, not its transactions — those are
  // fetched per pop-up by useLocTransactions below, which is why this state has to
  // be declared BEFORE that hook call rather than next to the handler that sets it.
  const [drill, setDrill] = useState<{
    referenceName: string
    /** Which reference's transactions to fetch — see {@link useLocTransactions}. */
    scope: LocTxnScope
    startingBalance: number
    // Show per-facility group headings only for a pack drill (attributes each
    // block to its member facility, even a single-member pack — DCP-169).
    showGroupHeadings: boolean
    /**
     * The LOC Usage figure the user clicked, straight from the drilled cell
     * (DCP-333) — the modal warns when it lists NO transactions at all under a
     * figure that implies some should exist (a partially-truncated feed is
     * caught separately, by the transactions hook's own row-count guard). Safe
     * to take from ANY period column: LOC Usage is period-INDEPENDENT.
     * The DCP-147 `loc_line_calc` / `loc_facility` / `loc_pack` /
     * `loc_member_usage` CTEs in
     * supabase/migrations/20260731000000_dcp147_cash_position_loc.sql are computed
     * WITHOUT `period_index` and joined per facility/pack, so every period column
     * repeats the same figure.
     */
    expectedTotal: number
  } | null>(null)

  // Real LINE OF CREDIT transactions behind the OPEN drill-down only (DCP-169,
  // rescoped by DCP-333): scoped server-side to the clicked facility/pack, and
  // not fetched at all (scope null) while the modal is closed. Replaces the
  // org-wide feed this page used to slice client-side, which PostgREST truncated
  // at 1000 rows and left later pop-ups empty.
  const locTxns = useLocTransactions(drill?.scope ?? null)

  // Ready-gate for the drill affordance (DCP-333). It no longer waits on the
  // transaction feed — that fetch now starts on click, and its loading/error
  // states live inside the modal. It waits on useLocAvailability INSTEAD, because
  // `startingBalance` below comes from `startingByRef`, which is built from
  // `locReferences`: offering the drill before that resolves would open a modal
  // whose Total omits the starting baseline, and the new reconciliation check
  // would then fire an amber "transactions may be missing" warning on perfectly
  // good data. `locReferences` is undefined while loading or errored, and an
  // array (possibly empty) once resolved.
  const drillReady = locReferences !== undefined

  /**
   * Opens the LOC Usage drill-down scoped to the clicked column (DCP-169). A
   * facility column (`column.key` IS the facility id) fetches that facility's
   * transactions as one group; a pack column (`pack:<id>` key) fetches its
   * members' as one group per member facility (AC.2). Both are resolved by the
   * RPC from the scope recorded here (DCP-333).
   *
   * `startingBalance` is the drilled reference's REAL Starting LOC Usage amount
   * from get_loc_availability (the same field LocAvailabilityPage shows): a
   * standalone facility's line starting, or the Σ of a pack's lines' starting. A
   * pack MEMBER column is never a reference there, so it resolves to 0 (member
   * usage is tx-only, per the BA). The modal Total (starting + Σtx) therefore
   * genuinely cross-checks against the clicked LOC Usage rather than reconciling
   * by construction as the earlier `locUsage − Σtx` residual did (PR #169 review).
   *
   * @param column - the clicked facility or pack-subtotal column
   * @param value - that cell's LOC Usage figure, reconciled against in the modal
   */
  const handleViewLocUsage = (column: CashPositionMultiPeriodColumn, value: number) => {
    // Reference id for the starting-balance lookup: a pack column keys as
    // `pack:<id>` (strip the prefix); a facility column IS the facility id.
    const refKey =
      column.kind === 'pack' ? column.key.slice(PACK_KEY_PREFIX.length) : column.key
    const startingBalance = startingByRef.get(refKey) ?? 0
    setDrill({
      referenceName: column.name,
      // A pack column scopes by its pack id (the RPC gathers every member
      // facility's rows); a facility column scopes by `column.key`, which IS the
      // facility id — including a pack MEMBER column, whose own transactions are
      // exactly what its cell shows (DCP-333).
      scope:
        column.kind === 'pack' ? { kind: 'pack', id: refKey } : { kind: 'facility', id: column.key },
      startingBalance,
      showGroupHeadings: column.kind === 'pack',
      expectedTotal: value,
    })
  }

  const [manage, setManage] = useState<ProjectionCellScope | null>(null)
  // The route only needs view_cash_position, which View Only and Accounts
  // Payable also hold; no RLS checks manage_projections, so this is the gate.
  const { data: profile } = useProfile()
  const canManageProjections = hasPermission(profile?.productRole, 'manage_projections')

  // Bounds from the report's own `periodMeta` (the windows the RPC was queried
  // with), so the modal lists what the cell sums even across a day rollover.
  const handleManageProjections = (
    column: CashPositionMultiPeriodColumn,
    periodIdx: number,
    row: ProjectionCellRow,
  ) => {
    if (!data) return
    const period = data.periodMeta[periodIdx]
    if (!period) return
    setManage({
      // Members from the same `data` as the clicked column (see projectionCellTarget).
      target: projectionCellTarget(column, data.columns),
      periodLabel: period.label,
      start: period.start,
      end: period.end,
      row,
    })
  }

  // Any load failure (summary or a catalog) surfaces as the error state — so a
  // failed facilities/packs fetch is NOT silently rendered as "No facilities" or
  // an empty filter (the catalogs also gate hasFacilities and the options).
  //
  // DCP-333 (Presto PR #184 finding 3): `locAvailabilityError` is deliberately
  // NOT folded in here. This chain renders an error card INSTEAD of the whole
  // grid (see the `{error && (` branch below), and the grid's own numbers come
  // from a different RPC (`get_cash_position_summary_multi_period`) that keeps
  // working fine when `get_loc_availability` fails — only the drill-down
  // affordance is affected. Blanking the entire forecast for that would be
  // disproportionate; a non-blocking notice near the grid covers it instead.
  const error = summaryError ?? facilitiesError ?? packsError

  // Header date line — ALWAYS the current date (DCP-293), formatted
  // "March 31, 2025 (Monday)". It marks when the report was RUN, not how fresh
  // the underlying balances are (it used to show the most-recent bank-balance
  // date, which read as the report being stale).
  //
  // The day comes from `businessDate()` (`@dcp/shared`), the same primitive that
  // anchors `cashPositionPeriods()` and the freshness checks, so the header can
  // never name a different day than the periods it labels or the balances it is
  // read against. `businessDate()` returns a bare `YYYY-MM-DD`, which dayjs parses
  // at local midnight — used here only to format, never to decide the day.
  // Recomputed every render so a long-lived tab rolls over with the business day
  // on the next data change.
  const today = dayjs(businessDate())
  const asOf = { date: today.format('MMMM D, YYYY'), weekday: today.format('dddd') }
  const asOfLabel = `${asOf.date} (${asOf.weekday})`

  // Combined Total is redundant when the filters bring the report down to a
  // single pack or a single facility (DCP-293) — see shouldShowCombinedTotal.
  // The unfiltered report always keeps it, whatever the org's shape.
  const filtered = filters.packIds.length > 0 || filters.facilityIds.length > 0
  const showCombinedTotal = !filtered || shouldShowCombinedTotal(data?.columns ?? [])

  // Which period indices the grid renders: all five for "All", else the single
  // selected index — looked up by key against the same period ladder that built
  // PERIOD_OPTIONS, so the index can never drift from the selector's options.
  // Memoized so the array identity is stable across renders.
  const visiblePeriodIndices = useMemo(
    () =>
      periodSelection === 'all'
        ? [0, 1, 2, 3, 4]
        : // Guard the lookup: a key that matches no period would yield -1 and index
          // an undefined period. Options are built from the same ladder so this is
          // currently unreachable, but the fallback to period 0 keeps it crash-safe.
          [Math.max(0, periods.findIndex((p) => p.key === periodSelection))],
    [periodSelection],
  )

  // Once packs are picked, only their facilities (and the pack-less ones under
  // "No Pack") can be shown, so only those are offered.
  const facilityOptions = useMemo(
    () =>
      facilitiesForPackSelection(activeFacilities, filters.packIds)
        .map((f) => ({ value: f.id, label: f.name }))
        .sort(byLabel),
    [activeFacilities, filters.packIds],
  )

  // Only offer packs that actually have an active member (an empty pack would
  // produce no columns), and "No Pack" only when an active facility has no pack.
  // Names come from the pack catalog.
  const packOptions = useMemo(() => {
    const memberPackIds = new Set(activeFacilities.map((f) => f.pack_id).filter(Boolean) as string[])
    const options = (packs ?? [])
      .filter((p) => memberPackIds.has(p.id))
      .map((p) => ({ value: p.id, label: p.name }))
      .sort(byLabel)
    if (activeFacilities.some((f) => f.pack_id == null)) {
      options.push({ value: NO_PACK, label: NO_PACK_LABEL })
    }
    return options
  }, [packs, activeFacilities])

  // Fold the catalog loads into the page skeleton so the grid + filters paint once.
  const loading = isLoading || packsLoading || facilitiesLoading || !data
  const hasFacilities = activeFacilities.length > 0

  // Filters + period selector only make sense once catalogs are loaded and the
  // org has facilities. Gate on the same condition as the grid so the header
  // controls appear exactly when the report does (DCP-306).
  const showControls = !error && !loading && hasFacilities
  // Filters that match no facility leave nothing to report: the grid shows its
  // "no match" message instead, and the download is withheld with it.
  const hasColumns = (data?.columns.length ?? 0) > 0

  return (
    <div className="space-y-4">
      {/* DCP-306: pack/facility filters live in the header's top-right `actions`
          slot — compact, out of the report's vertical space. */}
      <PageHeader
        title="Cash Position Summary"
        description="Cash flow forecast by facility and pack across the current week and the next four weeks"
        actions={
          showControls ? (
            <div className="flex items-end gap-3">
              <CashPositionFilters
                packIds={filters.packIds}
                facilityIds={tickedFacilityIds(filters, activeFacilities)}
                onPackIdsChange={(packIds) =>
                  setFilters(selectCashPositionPacks(filters, packIds, activeFacilities))
                }
                onFacilityIdsChange={(ticked) =>
                  setFilters(selectCashPositionFacilities(filters, ticked, activeFacilities))
                }
                onClear={() => setFilters(NO_CASH_POSITION_FILTERS)}
                packOptions={packOptions}
                facilityOptions={facilityOptions}
              />
              <CashPositionDownloadMenu
                getReport={() =>
                  buildCashPositionReport({ data, showCombinedTotal, visiblePeriodIndices, asOf })
                }
                orgName={currentOrganization?.name ?? ''}
                reportDate={today}
                filterText={cashPositionFilterText(
                  data?.columns ?? [],
                  periodSelection === 'all' ? null : periods[visiblePeriodIndices[0]].label,
                )}
                disabled={isPlaceholderData || !hasColumns}
              />
            </div>
          ) : undefined
        }
      />

      <Card>
        {/* Compact card chrome (DCP-306): tighter padding so the single-period
            report fits one screen without scrolling. */}
        <CardHeader className="p-4 pb-3">
          {/* Single "As of <date> (<weekday>)" line (DCP-306: dropped the
              redundant "Cash Position Summary:" prefix already shown by the page
              header, and made it smaller). The period selector shares this row on
              the right; per-period scope headings appear inside the table. */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-lg font-semibold">As of {asOfLabel}</CardTitle>
            {showControls && (
              // Named group so the period toggle keeps an accessible name now
              // that the visible "Period:" label was dropped for compactness —
              // PeriodSelector doesn't forward aria-label itself (DCP-306).
              <div role="group" aria-label="Period">
                <PeriodSelector
                  options={PERIOD_OPTIONS}
                  value={periodSelection}
                  onChange={(value) => setPeriodSelection(value as PeriodSelection)}
                />
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-0">
          {/* Error state — surface the query error message. */}
          {error && (
            <div className="flex h-48 items-center justify-center text-center">
              <div className="space-y-2">
                <p className="text-lg font-medium text-red-600">Failed to load cash position</p>
                <p className="text-sm text-gray-600">{(error as Error).message}</p>
              </div>
            </div>
          )}

          {/* Loading state — skeleton rows while the summaries compute. */}
          {!error && loading && (
            <div className="space-y-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          )}

          {/* Empty state — org has no facilities. */}
          {!error && !loading && !hasFacilities && (
            <div className="flex h-48 items-center justify-center text-center">
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">No facilities</p>
                <p className="text-sm text-gray-600">
                  Add a facility in Settings to view its cash position.
                </p>
              </div>
            </div>
          )}

          {/* DCP-333 (Presto PR #184 finding 3): non-blocking notice for a failed
              `useLocAvailability` fetch. Without this, the "View LOC
              transactions" buttons disappear from every column with no
              explanation and no way to retry short of a reload — the gate
              itself (`drillReady`) is correct (drilling without the real
              starting baseline would show a Total missing it and trip the
              reconciliation warning on good data), but silently vanishing is
              not. `role="status"` — informational, not blocking the page.

              DCP-333 (PR #184 human review): gated on `!locReferences` — the
              SAME condition as `drillReady`, inverted — because the notice and
              the affordance must be driven by one condition or the page
              contradicts itself. React Query v5 keeps `data` across a failed
              BACKGROUND refetch (which the borrowing-base mutations trigger by
              invalidating `locAvailability`), so on the error alone this notice
              claimed the drill-down was unavailable while `drillReady` stayed
              true and the View icons right below it rendered and worked. With
              the data retained the drill genuinely still works — on the
              previous starting balances — so `drillReady` is left as it is and
              only the notice is narrowed. */}
          {showControls && locAvailabilityError && !locReferences && (
            <div
              role="status"
              className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                LOC Usage drill-down is unavailable — the LOC starting balances could not be
                loaded. Reload the page to retry.
              </span>
            </div>
          )}

          {/* DCP-161 (PR #182 review): make a DEGRADED freshness fetch visible.

              `useCashPositionFreshness` degrades to silence on purpose — a signal
              we do not have must never render as missing DATA — and `partial`
              already shortens the stale window so the silence is not cached. But
              silence is also exactly what a perfectly clean report looks like, and
              nothing surfaced the difference: one failed round trip stripped every
              warning off the grid while the figures kept rendering, so the reader
              concluded every facility's balances were present. That is the very
              conflation this feature exists to remove, reappearing one level up.

              Deliberately NOT amber. On this report amber now means "this cell's
              data is missing"; reusing it for "we could not check" would put the
              conflation back in a new place. Neutral + `role="status"`, the same
              non-blocking shape as the LOC notice above.

              Gated on `freshness?.partial` — the same object the warnings are built
              from — so the notice and the (absent) icons are driven by one condition
              and the page cannot contradict itself. */}
          {showControls && freshness?.partial && (
            <div
              role="status"
              className="mb-3 flex items-start gap-2 rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground"
            >
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Missing-data checks are unavailable — the balance-coverage signals could not be
                loaded, so cells below are shown without warnings. Reload the page to retry.
              </span>
            </div>
          )}

          {/* Loaded — the grid (period selector + filters now live in the
              headers above, per DCP-306). Dimmed + non-interactive while a
              filter/period change refetches over the stale result, so the
              background load is visible rather than looking like a no-op. */}
          {showControls && (
            <div
              className={
                isPlaceholderData
                  ? 'pointer-events-none opacity-60 transition-opacity'
                  : 'transition-opacity'
              }
            >
              {hasColumns ? (
                <CashPositionSummaryGrid
                  columns={data.columns}
                  combinedTotal={data.combinedTotal}
                  periodMeta={data.periodMeta}
                  visiblePeriodIndices={visiblePeriodIndices}
                  // Gate the drill affordance until the LOC starting baselines are
                  // ready (undefined handler → the grid renders no View button) —
                  // see `drillReady` for why that, and not the transactions, is
                  // now the prerequisite (DCP-333).
                  onViewLocUsage={drillReady ? handleViewLocUsage : undefined}
                  showCombinedTotal={showCombinedTotal}
                  // DCP-161: missing-data icons on the Bank Balances / Intacct
                  // Balance cells. An empty map (still loading, or a failed
                  // freshness fetch) simply renders no icons.
                  warnings={warnings}
                  onManageProjections={canManageProjections ? handleManageProjections : undefined}
                />
              ) : (
                // Filters selected but nothing matches — keep the bars visible so
                // the user can adjust or clear.
                <div className="flex h-32 items-center justify-center text-center">
                  <p className="text-sm text-gray-600">No facilities match the selected filters.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* LOC Usage drill-down (DCP-169) — the real LINE OF CREDIT transactions
          behind the clicked facility/pack column's usage, fetched for that column
          only while this modal is open (DCP-333). The dialog prefixes
          "LOC Usage for …" onto referenceName. */}
      {drill && (
        <ViewBankTransactionsDialog
          open
          onClose={() => setDrill(null)}
          referenceName={drill.referenceName}
          groups={locTxns.data ?? []}
          // `isLoading`, NOT `isPending`: in React Query v5 `isPending` only means
          // "no data yet", so it is also true for a DISABLED query — it would read
          // as loading for a column nobody drilled. `isLoading` is
          // `isPending && isFetching`, i.e. true only while the first real fetch
          // for this scope is in flight, and false during a background refetch
          // over cached rows (so an inline Notes save doesn't blank the table).
          isLoading={locTxns.isLoading}
          // Only surface the error when there is genuinely nothing to show (DCP-333).
          // A failed background refetch sets `isError` while React Query v5 KEEPS the
          // previous `data` in cache — gating on `isError` alone would blank a
          // populated table on a transient error, worst of all right after a Notes
          // save (its invalidation triggers exactly this refetch). With `!locTxns.data`
          // the stale-but-valid rows stay visible and the query recovers on the next
          // successful refetch; a genuine first-load failure still has no data, so it
          // still shows the message.
          errorMessage={locTxns.isError && !locTxns.data ? locTxns.error.message : undefined}
          // The other half of the errorMessage decision above (DCP-333): nothing
          // cached shows the error IN PLACE of the rows (errorMessage), something
          // cached keeps the rows and warns BESIDE them (refreshFailed) — a failed
          // background refetch is never silent either way.
          refreshFailed={locTxns.isError && !!locTxns.data}
          onRetry={() => void locTxns.refetch()}
          retrying={locTxns.isFetching}
          // Suppress the modal's own missing-transactions check while EITHER
          // source of the two figures it compares is still fetching (DCP-333):
          // `expectedTotal` (this cell's value) comes from the summary RPC, and
          // `startingBalance` comes from `useLocAvailability` via `startingByRef`.
          // The two queries invalidate together but land at different times, and
          // `startingLocUsageAmount` is additionally written optimistically by
          // useUpdateBorrowingBase / useUpdatePackBorrowingBase ahead of the
          // summary's own recompute — so during that window a facility with no
          // LOC transactions can show a transient mismatch that is not a real
          // data-integrity problem. `undefined` already means "skip the check" to
          // the modal, so withhold the figure until BOTH queries have settled.
          expectedTotal={
            summaryFetching || locAvailabilityFetching ? undefined : drill.expectedTotal
          }
          showGroupHeadings={drill.showGroupHeadings}
          startingBalance={drill.startingBalance}
        />
      )}

      {/* Mounted only while open, keyed per cell, so drafts start empty. */}
      {manage && (
        <ManageProjectionsDialog
          key={`${cellTargetKey(manage.target)}:${manage.start}:${manage.row.kind === 'outflows' ? 'outflows' : manage.row.name}`}
          scope={manage}
          onClose={() => setManage(null)}
        />
      )}
    </div>
  )
}
